import { CartAddEvent } from '@theme/events';

/**
 * Cart drawer "You May Also Like" recommendations.
 *
 * Starts hidden. Fetches genuine Shopify recommendations (the `cart-recommendations`
 * section via the Product Recommendations route, seeded by the first cart item),
 * swaps the skeleton for a scroll-snap carousel of cards, and reveals itself. When
 * Shopify returns no recommendations the endpoint responds empty and the block
 * stays hidden. Each card can pick a variant and add it to the cart in place.
 */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    // Delegated so it covers the cards injected after the fetch.
    this.addEventListener('click', this.#onClick);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-recs-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    this.#addToCart(button);
  };

  /**
   * Adds the card's selected variant to the cart, then refreshes the drawer.
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    if (button.disabled) return;

    const card = button.closest('.cart-recommendations__card');
    const select = card?.querySelector('[data-recs-select]');
    const variantId = select instanceof HTMLSelectElement ? select.value : button.dataset.variantId;
    if (!variantId) return;

    const sectionIds = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    }

    button.disabled = true;
    button.dataset.adding = 'true';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: [...sectionIds].join(','),
          sections_url: window.location.pathname,
        }),
      }).then((res) => res.json());

      if (response.status) return; // e.g. sold out — leave the button usable

      // Fires `cart:update`, which re-renders the cart items and opens the drawer.
      this.dispatchEvent(
        new CartAddEvent(response, this.id || 'cart-recommendations', {
          source: 'cart-recommendations',
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
      delete button.dataset.adding;
    }
  }

  async #load() {
    const productId = this.dataset.productId;
    const baseUrl = this.dataset.url;

    if (!productId || !baseUrl) return;

    this.dataset.loaded = 'true';

    try {
      const response = await fetch(`${baseUrl}&product_id=${encodeURIComponent(productId)}`);
      if (!response.ok) return;

      const text = await response.text();
      if (!text.trim()) return;

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const list = doc.querySelector('[data-recs-list]');
      const target = this.querySelector('[data-recs-target]');

      // Hide when Shopify returned no recommendations.
      if (!list || list.children.length === 0 || !target) {
        this.hidden = true;
        return;
      }

      // Swap the skeleton for the carousel via an HTML string (no cross-document
      // node adoption), reveal, then wire up the dot pagination.
      target.outerHTML = list.outerHTML;
      this.hidden = false;
      this.#initCarousel();
    } catch (error) {
      this.hidden = true;
    }
  }

  #initCarousel() {
    const track = this.querySelector('[data-recs-track]');
    const dots = Array.from(this.querySelectorAll('.cart-recommendations__dot'));
    if (!track || dots.length === 0) return;

    const slides = Array.from(track.children);

    // Dot click → scroll the matching slide into view.
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        slides[index]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
    });

    // Scroll position → highlight the active dot.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(entry.target);
          dots.forEach((dot, i) => {
            if (i === index) {
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.removeAttribute('aria-current');
            }
          });
        }
      },
      { root: track, threshold: 0.6 }
    );

    slides.forEach((slide) => observer.observe(slide));
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
